import { useSelector } from 'react-redux';
import { ALERT_MESSAGES, STATUS } from '../../util/constants';
import {
  getApplicationDocumentsState,
  getDocumentFilesState,
  getNavSearchResults,
  getStudentState,
  getUpdateManageReviewerState,
  getCommitteeReviewersFromDBState,
  getActiveReviewerState,
  getNextAppState,
  getOverrideRecommendationState,
  getReportsState,
  getReportAthenaCustomState,
  getExportReportsState,
  getHoldForAdminState,
} from '../redux/selectors';
import { RESET_REPORTSALERTS, RESET_REPORTSFILTERALERTS } from '../../actions-index';

/**
 * Helper: returns a shallow copy of the state with the alert MESSAGE overridden.
 * Avoids mutating frozen Redux/Immer state objects.
 */
function withAlertMessage(stateSlice, message) {
  if (stateSlice.status && stateSlice?.alert?.MESSAGE) {
    return {
      ...stateSlice,
      alert: { ...stateSlice.alert, MESSAGE: message },
    };
  }
  return stateSlice;
}

export const useAlertBarStatus = () => {
  let alerts = [];

  const studentState = useSelector((state) => {
    const s = getStudentState(state);
    const msg = s.status === STATUS.SUCCESS ? ALERT_MESSAGES.SUCCESS_UPDATE : ALERT_MESSAGES.ERROR_RETRIEVING_APPLICATION;
    return withAlertMessage(s, msg);
  });

  const applicationDocumentsState = useSelector((state) => {
    const s = getApplicationDocumentsState(state);
    const msg = s.status === STATUS.SUCCESS ? ALERT_MESSAGES.SUCCESS_UPDATE : ALERT_MESSAGES.ERROR_RETRIEVING_DOCUMENTS;
    return withAlertMessage(s, msg);
  });

  const documentFilesState = useSelector((state) => {
    const s = getDocumentFilesState(state);
    let msg = ALERT_MESSAGES.SUCCESS_UPDATE;
    if (s.status !== STATUS.SUCCESS) {
      msg = s?.alert?.ALERT_BAR_MESSAGE ?? ALERT_MESSAGES.ERROR_RETRIEVING_DOCUMENT;
    }
    return withAlertMessage(s, msg);
  });

  const navbarSearchState = useSelector((state) => {
    const s = getNavSearchResults(state);
    const msg = s.status === STATUS.SUCCESS ? ALERT_MESSAGES.SUCCESS_UPDATE : ALERT_MESSAGES.ERROR_RETRIEVING_SEARCH_RESULTS;
    return withAlertMessage(s, msg);
  });

  const committeeReviewersState = useSelector((state) => {
    const s = getCommitteeReviewersFromDBState(state);
    const msg = s.status === STATUS.SUCCESS ? ALERT_MESSAGES.SUCCESS_UPDATE : ALERT_MESSAGES.ERROR_RETRIEVING_COMMITTEE_REVIEWERS;
    return withAlertMessage(s, msg);
  });

  const nextAppState = useSelector((state) => {
    const s = getNextAppState(state);
    const msg = s.status === STATUS.SUCCESS ? ALERT_MESSAGES.SUCCESS_UPDATE : ALERT_MESSAGES.ERROR_RETRIEVING_APPLICATION;
    return withAlertMessage(s, msg);
  });

  const activeReviewerState = useSelector((state) => {
    const s = getActiveReviewerState(state);
    const msg = s.status === STATUS.SUCCESS ? ALERT_MESSAGES.SUCCESS_UPDATE : ALERT_MESSAGES.ERROR_UPDATE;
    return withAlertMessage(s, msg);
  });

  const updateManageReviewerState = useSelector((state) => {
    const s = getUpdateManageReviewerState(state);
    const msg = s.status === STATUS.SUCCESS ? ALERT_MESSAGES.SUCCESS_UPDATE : ALERT_MESSAGES.ERROR_UPDATE;
    return withAlertMessage(s, msg);
  });

  const overrideState = useSelector((state) => {
    const s = getOverrideRecommendationState(state);
    const msg = s.status === STATUS.SUCCESS ? ALERT_MESSAGES.SUCCESS_UPDATE : ALERT_MESSAGES.ERROR_OVERRIDING_APPLICATION;
    return withAlertMessage(s, msg);
  });

  const reportsState = useSelector((state) => {
    const s = getReportsState(state);
    const reportDataError = s.status !== STATUS.SUCCESS ? s?.alert?.MESSAGE : undefined;
    return {
      status: reportDataError ? STATUS.ERROR : undefined,
      alert: { MESSAGE: reportDataError },
      type: RESET_REPORTSALERTS,
    };
  });

  const reportsFiltersState = useSelector((state) => {
    const s = getReportsState(state);
    const reportCustomFilterError = s.data?.filterOptionsCustomStatus !== STATUS.SUCCESS ? s?.data?.customFiltersErrorAlert?.MESSAGE : undefined;
    return {
      status: reportCustomFilterError ? STATUS.ERROR : undefined,
      alert: { MESSAGE: reportCustomFilterError },
      type: RESET_REPORTSFILTERALERTS,
    };
  });

  const reportAthenaCustomState = useSelector((state) => {
    const s = getReportAthenaCustomState(state) ?? {};
    const msg = s?.status === STATUS.SUCCESS ? undefined : s?.alert?.MESSAGE;
    return withAlertMessage(s, msg);
  });

  const exportReportState = useSelector((state) => {
    const s = getExportReportsState(state) ?? {};
    const msg = s?.status === STATUS.SUCCESS ? undefined : s?.alert?.MESSAGE;
    return withAlertMessage(s, msg);
  });

  const holdForAdminState = useSelector((state) => {
    const s = getHoldForAdminState(state);
    const msg = s?.alert?.MESSAGE;
    return withAlertMessage(s, msg);
  });

  const handleAlerts = (state) => {
    if (state?.status && state?.alert?.MESSAGE) {
      if (!alerts.some((alert) => JSON.stringify(alert) === JSON.stringify(state))) {
        alerts.push(state);
      }
    } else if (!state?.status) {
      alerts = alerts.filter((alert) => alert.type !== state.type);
    }
  };

  handleAlerts(studentState);
  handleAlerts(applicationDocumentsState);
  handleAlerts(documentFilesState);
  handleAlerts(navbarSearchState);
  handleAlerts(nextAppState);
  handleAlerts(updateManageReviewerState);
  handleAlerts(committeeReviewersState);
  handleAlerts(activeReviewerState);
  handleAlerts(overrideState);
  handleAlerts(reportsState);
  handleAlerts(reportsFiltersState);
  handleAlerts(reportAthenaCustomState);
  handleAlerts(exportReportState);
  handleAlerts(holdForAdminState);

  return alerts;
};
